<div class="h-screen overflow-y-auto surface-200 pb-4" data-testid="client-forms-container">
  <div class="p-2 flex align-items-center justify-content-between border-bottom-1 border-300" data-testid="forms-header">
    <div class="my-1 pl-2 text-xs md:text-sm" data-testid="forms-title">
      @if (!loading()) {
        <strong class="text-800" aria-label="forms-title">{{ 'CLIENT_PROFILE.TABS.FORMS' | translate }}</strong>
      }
    </div>
    @if (!loading()) {
      <div data-testid="new-form-button-section" class="flex gap-2">
        <p-button
          icon="pi pi-plus"
          [label]="'CLIENT_PROFILE.FORMS.NEW_FORM' | translate"
          (onClick)="openNewFormDialog()"
          data-testid="new-form-button"
          aria-label="new-form-button"
        />
      </div>
    }
  </div>

  @if (loading()) {
    <div class="p-3" data-testid="forms-skeleton">
      <p-skeleton height="2rem" class="mb-2" />
      <p-skeleton height="10rem" />
    </div>
  }

  @if (!loading()) {
    <div class="surface-0 p-3" data-testid="forms-content">
      <app-data-grid
        [hidden]="loading()"
        [columnData]="tableData$"
        [loading]="loading()"
        [columns]="columns$"
        [totalRecords]="totalRecordLength$"
        [paginator]="false"
        [EmptyState]="emptyStateComponent$"
        [selectable]="selectAllData$"
        [selectionMode]="'single'"
        [resizableColumns]="true"
        [columnResizeMode]="'expand'"
        (onAction)="handleAction($event)"
        (emitValue)="onRowSelect($event)"
        data-testid="forms-grid"
        [gridName]="'client-forms-list'"
      />
    </div>
  }
</div>

<!-- New Form Dialog (Template Select + CTAs) -->
<p-dialog
  [header]="'CLIENT_PROFILE.FORMS.NEW_FORM' | translate"
  [visible]="showNewFormDialog()"
  [modal]="true"
  [closable]="true"
  [appendTo]="'body'"
  [resizable]="false"
  [draggable]="false"
  [breakpoints]="{ '767px': '95vw', '576px': '100vw' }"
  [style]="{ width: '28rem' }"
  (onHide)="closeNewFormDialog()"
  contentStyleClass="p-3 md:p-4"
  data-testid="new-form-dialog"
  aria-label="new-form-dialog"
>
  <div class="flex flex-column gap-4" data-testid="template-select-section">
    <div>
      <label for="template-dropdown" class="font-medium block mb-2 text-sm md:text-base">{{ 'CLIENT_PROFILE.FORMS.SELECT_TEMPLATE' | translate }}</label>
      <p-select
        id="template-dropdown"
        [options]="staffFillableTemplates()"
        optionLabel="title"
        optionValue="id"
        [appendTo]="'body'"
        [placeholder]="'CLIENT_PROFILE.FORMS.SELECT_TEMPLATE_PLACEHOLDER' | translate"
        [formControl]="templateSelectControl"
        styleClass="w-full min-h-3rem text-sm md:text-base"
        data-testid="template-dropdown"
        aria-label="template-dropdown"
      />
    </div>

    <div class="flex flex-column sm:flex-row gap-2 sm:justify-content-end pt-3 border-top-1 border-200">
      <p-button
        [label]="'CLIENT_PROFILE.FORMS.FILL_NOW' | translate"
        icon="pi pi-pencil"
        [disabled]="!selectedTemplateId()"
        [loading]="openingFillDrawer()"
        (onClick)="onFillNow()"
        styleClass="w-full sm:w-auto min-h-3rem order-2 sm:order-1"
        [pTooltip]="!selectedTemplateId() ? ('CLIENT_PROFILE.FORMS.SELECT_TEMPLATE_FIRST' | translate) : null"
        data-testid="fill-now-button"
        aria-label="fill-now-button"
      />
      <p-button
        [label]="'CLIENT_PROFILE.FORMS.SEND_LINK' | translate"
        icon="pi pi-send"
        [disabled]="!selectedTemplateId()"
        [loading]="sendingLink()"
        (onClick)="openSendLinkChannelPopup()"
        severity="secondary"
        styleClass="w-full sm:w-auto min-h-3rem order-1 sm:order-2"
        [pTooltip]="!selectedTemplateId() ? ('CLIENT_PROFILE.FORMS.SELECT_TEMPLATE_FIRST' | translate) : null"
        data-testid="send-link-button"
        aria-label="send-link-button"
      />
    </div>
  </div>
</p-dialog>

<!-- Send Link - Notification Channel Popup -->
<p-dialog
  [visible]="showSendLinkChannelPopup()"
  [modal]="true"
  [closable]="true"
  [appendTo]="'body'"
  [resizable]="false"
  [header]="'CLIENT_PROFILE.FORMS.SEND_LINK' | translate"
  (onHide)="closeSendLinkChannelPopup()"
  [breakpoints]="{ '576px': '95vw' }"
  [style]="{ width: '28rem' }"
  contentStyleClass="p-0"
  data-testid="send-link-channel-popup"
  aria-label="send-link-channel-popup"
>
  <ng-template pTemplate="content">
    <div class="p-4">
      <p class="text-sm text-600 mb-4 line-height-3">
        {{ 'CLIENT_PROFILE.FORMS.SEND_LINK_DESCRIPTION' | translate }}
      </p>

      <label class="text-sm text-700 mb-3 block font-medium">{{ 'CLIENT_PROFILE.FORMS.SEND_ON' | translate }}</label>

      <div class="flex flex-column gap-2">
        @for (channel of notificationChannels; track channel.value) {
          <button
            type="button"
            [ngClass]="isChannelSelected(channel.value) ? 'p-button-primary' : 'p-button-outlined surface-border'"
            (click)="toggleSendLinkChannel(channel.value)"
            class="w-full justify-content-start px-3 py-3 border-1 border-round"
            pButton
            pRipple
            [attr.aria-label]="'channel-' + channel.value"
            [attr.data-testid]="'channel-' + channel.value"
          >
            <div class="flex align-items-center gap-3 w-full">
              <i [class]="getChannelIcon(channel.value) + ' text-lg'" [ngClass]="isChannelSelected(channel.value) ? 'text-white' : 'text-primary'"></i>
              <span class="flex-1 text-left text-sm font-medium">{{ channel.label }}</span>
              @if (isChannelSelected(channel.value)) {
                <i class="fa fa-check-circle text-sm text-white" aria-hidden="true"></i>
              }
            </div>
          </button>
        }
      </div>

      @if (getSelectedChannelsCount() > 0) {
        <div class="mt-3 p-2 surface-50 border-round flex align-items-center gap-2">
          <i class="fa fa-info-circle text-primary text-sm" aria-hidden="true"></i>
          <span class="text-xs text-600">
            {{ getSelectedChannelsCount() }} {{ 'CLIENT_PROFILE.FORMS.CHANNELS_SELECTED' | translate }}
          </span>
        </div>
      }
    </div>

    <div class="px-4 pb-4 pt-3 border-top-1 border-200 flex align-items-center justify-content-end gap-2">
      <p-button
        [label]="'common.BUTTONS.CANCEL-BUTTON-TEXT' | translate"
        styleClass="p-button-text p-button-sm"
        (onClick)="closeSendLinkChannelPopup()"
        aria-label="cancel-send-link"
        data-testid="cancel-send-link"
      />
      <p-button
        [label]="'SUBSCRIPTIONS.BTN_SEND' | translate"
        [loading]="sendingLink()"
        [disabled]="getSelectedChannelsCount() === 0"
        styleClass="p-button-sm"
        (onClick)="onSendLink()"
        aria-label="send-link-submit"
        data-testid="send-link-submit"
      />
    </div>
  </ng-template>
</p-dialog>

<!-- Fill Now Drawer -->
<p-drawer
  [visible]="showFillDrawer()"
  [position]="'right'"
  [style]="{ width: 'min(90vw, 42rem)' }"
  [blockScroll]="true"
  [dismissible]="false"
  [showCloseIcon]="true"
  [appendTo]="'body'"
  (onHide)="closeFillDrawer()"
  styleClass="form-fill-drawer p-0"
  data-testid="form-fill-drawer"
  aria-label="form-fill-drawer"
>
  <ng-template pTemplate="content">
    @if (drawerTemplateSchema()) {
      <div class="p-2 overflow-y-auto h-full">
        <lib-staff-form-fill
          [tenantId]="tenantId().toString()"
          [responseId]="drawerResponseId() ?? ''"
          [responseStatus]="drawerResponseStatus() ?? ''"
          [templateId]="drawerTemplateId() ?? ''"
          [clientId]="clientId().toString()"
          [templateSchema]="drawerTemplateSchema()!"
          [currentData]="drawerResponseData()"
          [readonly]="drawerReadonly()"
          (formCompleted)="onFormCompleted()"
          (formError)="onFormError()"
          data-testid="staff-form-fill"
        />
      </div>
    }
  </ng-template>
</p-drawer>

<!-- Confirmation Dialog for Delete -->
<p-confirmDialog aria-label="delete-confirmation-dialog" data-testid="delete-confirmation-dialog" />
